/**
 * WooCommerce Detection & Tracking Plugin
 *
 * Detects when SDK is running on a WooCommerce storefront and:
 *
 * 1. Captures WooCommerce-specific context (store URL, currency, cart hash, customer ID)
 * 2. Enriches track/identify/page events with WooCommerce metadata
 * 3. Provides identity signals for WooCommerce customers
 * 4. Fires e-commerce track events based on page data injected by the
 *    Segmentflow Connect WordPress plugin (`window.__sf_wc`)
 * 5. Binds checkout email capture listeners (classic blur + Blocks MutationObserver)
 *
 * Cart mutation events (add_to_cart, remove_from_cart) are handled server-side
 * by PHP hooks in class-segmentflow-wc-server-events.php. Order lifecycle
 * events are handled by WooCommerce webhooks in the backend.
 *
 * Detection relies on WooCommerce-specific JavaScript globals that are present
 * on storefronts when WooCommerce enqueues its frontend scripts. The plugin
 * also reads context injected by the Segmentflow Connect WordPress plugin
 * via the `segmentflow_tracking_context` PHP filter.
 *
 * This plugin should be registered before any events are sent.
 * Only one platform plugin (Shopify or WooCommerce) will activate per page.
 */

import type { Plugin } from "./plugin.js";
import type { SegmentflowWeb } from "../web.js";
import { writeSfId } from "../web.js";

// ---------------------------------------------------------------------------
// WooCommerce global type declarations
// ---------------------------------------------------------------------------

/**
 * WooCommerce JavaScript params injected by WooCommerce on the frontend.
 * These globals indicate a WooCommerce storefront is active.
 */
interface WooCommerceParams {
  ajax_url?: string;
  wc_ajax_url?: string;
}

/**
 * WooCommerce cart fragments params (present when cart fragments are enabled)
 */
interface WcCartFragmentsParams {
  ajax_url?: string;
  wc_ajax_url?: string;
  cart_hash_key?: string;
  fragment_name?: string;
}

/**
 * Context injected by the Segmentflow Connect WordPress plugin.
 * Set via the `segmentflow_tracking_context` PHP filter and rendered
 * as `window.__segmentflow_integration_context` by the tracking class.
 */
interface SegmentflowIntegrationContext {
  platform?: string;
  traits?: Record<string, unknown>;
  context?: {
    store_url?: string;
    currency?: string;
    cart_hash?: string | null;
    /** WC session customer ID — stable session-scoped identifier. */
    session_id?: string | null;
  };
}

// ---------------------------------------------------------------------------
// Page data injected by Segmentflow Connect PHP (window.__sf_wc)
// ---------------------------------------------------------------------------

/**
 * Structured WooCommerce page data injected by the Segmentflow Connect
 * WordPress plugin via `class-segmentflow-wc-tracking.php`.
 *
 * The PHP plugin renders `window.__sf_wc = { ... }` at `wp_head` priority 0,
 * before the SDK loads at priority 1. This data allows the SDK to fire
 * authoritative e-commerce events without DOM scraping.
 */
interface SfWcPageData {
  page:
    | "product"
    | "cart"
    | "checkout"
    | "thankyou"
    | "shop"
    | "category"
    | "other";
  currency: string;
  product?: {
    id: number;
    name: string;
    price: string;
    sku: string;
    categories: string[];
    image_url: string;
    url: string;
    type: string;
  };
  cart?: {
    items: Array<{
      cart_item_key?: string;
      product_id: number;
      variation_id: number;
      name: string;
      quantity: number;
      price: string;
      sku: string;
      image_url: string;
      url: string;
    }>;
    total: string;
    subtotal: string;
    item_count: number;
    cart_hash: string;
  };
}

/**
 * Extended window interface for WooCommerce globals
 */
interface WooCommerceWindow extends Window {
  woocommerce_params?: WooCommerceParams;
  wc_cart_fragments_params?: WcCartFragmentsParams;
  __segmentflow_integration_context?: SegmentflowIntegrationContext;
  __sf_wc?: SfWcPageData;
}

/**
 * WooCommerce context captured by the plugin
 */
export interface WooCommerceContext {
  isWooCommerce: boolean;
  storeUrl?: string;
  currency?: string;
  cartHash?: string;
  customerId?: string;
  /** WC session customer ID — stable, session-scoped (like Shopify's cart_token). */
  sessionId?: string;
}

// ---------------------------------------------------------------------------
// Detection helpers
// ---------------------------------------------------------------------------

/**
 * Detect if running on a WooCommerce storefront by checking for
 * WooCommerce-specific JavaScript globals.
 */
function detectWooCommerce(): boolean {
  if (typeof window === "undefined") return false;
  const win = window as WooCommerceWindow;
  return (
    typeof win.woocommerce_params !== "undefined" ||
    typeof win.wc_cart_fragments_params !== "undefined" ||
    typeof win.__sf_wc !== "undefined" ||
    (typeof win.__segmentflow_integration_context !== "undefined" &&
      win.__segmentflow_integration_context?.platform === "woocommerce")
  );
}

/**
 * Get WooCommerce context from window globals and plugin-injected context.
 */
function getWooCommerceContext(): WooCommerceContext {
  if (typeof window === "undefined") {
    return { isWooCommerce: false };
  }

  const win = window as WooCommerceWindow;

  if (!detectWooCommerce()) {
    return { isWooCommerce: false };
  }

  // Read context injected by the Segmentflow Connect WordPress plugin
  const pluginContext = win.__segmentflow_integration_context;

  const storeUrl = pluginContext?.context?.store_url ?? undefined;
  const currency = pluginContext?.context?.currency ?? undefined;
  const cartHash = pluginContext?.context?.cart_hash ?? undefined;
  const sessionId = pluginContext?.context?.session_id ?? undefined;

  // Customer ID from plugin context traits
  const rawCustomerId = pluginContext?.traits?.woocommerce_customer_id;
  const customerId =
    rawCustomerId != null && rawCustomerId !== 0
      ? `wc_${String(rawCustomerId)}`
      : undefined;

  return {
    isWooCommerce: true,
    storeUrl,
    currency,
    cartHash: cartHash ?? undefined,
    customerId,
    sessionId: sessionId ?? undefined,
  };
}

// ---------------------------------------------------------------------------
// Plugin implementation
// ---------------------------------------------------------------------------

/**
 * WooCommerce Plugin Implementation
 *
 * In addition to context enrichment (existing), the plugin now reads
 * `window.__sf_wc` (injected by the Segmentflow Connect WordPress plugin)
 * and fires e-commerce track events:
 *
 * Page-load events (fired in `load()`):
 * - product_viewed — single product page
 * - cart_viewed — cart page
 * - checkout_started — checkout page
 * Interaction events (via DOM listeners bound in `load()`):
 * - checkout_email_captured — email field blur/input on checkout
 */
export class WooCommercePlugin implements Plugin {
  name = "woocommerce";

  private context: WooCommerceContext = { isWooCommerce: false };

  /**
   * Load plugin, detect WooCommerce, fire page-load events, and bind listeners.
   */
  load(analytics: SegmentflowWeb): void {
    this.context = getWooCommerceContext();

    if (!this.context.isWooCommerce) {
      return; // Not a WooCommerce store, nothing to do
    }

    console.debug("[Segmentflow] WooCommerce detected", {
      storeUrl: this.context.storeUrl,
      currency: this.context.currency,
      cartHash: this.context.cartHash ? "present" : "absent",
      customerId: this.context.customerId,
    });

    // Read page data injected by the Segmentflow Connect WordPress plugin
    const win = window as WooCommerceWindow;
    const data = win.__sf_wc;
    if (!data) return;

    // Fire page-load events based on page type
    this.firePageLoadEvents(analytics, data);

    // Bind interactive event listeners
    //
    // Cart mutation events (add_to_cart, remove_from_cart) are now handled
    // server-side by PHP hooks in class-segmentflow-wc-server-events.php.
    // The SDK only handles email capture on the checkout page.
    //
    // Email capture: Blocks checkout renders asynchronously (needs MutationObserver),
    // classic checkout has #billing_email in the initial DOM (needs blur listener).
    if (this.isBlocksPage()) {
      this.bindBlocksCheckoutEmailListener(analytics, data);
    } else {
      this.bindCheckoutEmailListener(analytics, data);
    }
  }

  /**
   * Fire page-load track events based on the current WooCommerce page type.
   */
  private firePageLoadEvents(
    analytics: SegmentflowWeb,
    data: SfWcPageData,
  ): void {
    switch (data.page) {
      case "product":
        if (data.product) {
          analytics.track({
            event: "product_viewed",
            properties: {
              product_id: data.product.id,
              name: data.product.name,
              price: data.product.price,
              sku: data.product.sku,
              categories: data.product.categories,
              image_url: data.product.image_url,
              url: data.product.url,
              currency: data.currency,
            },
          });
        }
        break;

      case "cart":
        if (data.cart) {
          analytics.track({
            event: "cart_viewed",
            properties: {
              items: data.cart.items,
              total: data.cart.total,
              item_count: data.cart.item_count,
              currency: data.currency,
            },
          });
        }
        break;

      case "checkout":
        if (data.cart) {
          analytics.track({
            event: "checkout_started",
            properties: {
              items: data.cart.items,
              total: data.cart.total,
              item_count: data.cart.item_count,
              currency: data.currency,
            },
          });
        }
        break;
    }
  }

  /**
   * Bind email field blur listener on checkout pages.
   *
   * When a user enters their email on the checkout page, fires both
   * `identify()` (to associate the anonymous visitor with an email) and
   * `checkout_email_captured` (to track the funnel step).
   *
   * Handles both classic checkout (`#billing_email`) and Blocks-based
   * checkout (`input[id*="email"][autocomplete="email"]`) via fallback
   * selectors.
   */
  private bindCheckoutEmailListener(
    analytics: SegmentflowWeb,
    data: SfWcPageData,
  ): void {
    if (data.page !== "checkout") return;

    const selectors = [
      "#billing_email", // Classic checkout
      'input[id*="email"][autocomplete="email"]', // Blocks checkout
    ];

    let emailField: HTMLInputElement | null = null;
    for (const selector of selectors) {
      emailField = document.querySelector<HTMLInputElement>(selector);
      if (emailField) break;
    }

    if (!emailField) return;

    let lastCapturedEmail = "";
    const capturedField = emailField;

    const captureEmail = (): void => {
      const email = capturedField.value.trim();
      if (!email || email === lastCapturedEmail) return;
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;

      lastCapturedEmail = email;

      // Update sf_id cookie immediately so PHP has the email on next navigation.
      writeSfId({ e: email });

      analytics.identify({
        traits: { email },
      });

      analytics.track({
        event: "checkout_email_captured",
        properties: {
          email,
          currency: data.currency,
          cart_total: data.cart?.total,
          item_count: data.cart?.item_count,
        },
      });
    };

    capturedField.addEventListener("blur", captureEmail);

    // If the field is already populated (e.g., returning customer with
    // saved billing email), capture immediately without waiting for
    // user interaction.
    if (capturedField.value.trim()) {
      captureEmail();
    }
  }

  /**
   * Detect if the current page uses WooCommerce Blocks.
   *
   * WooCommerce Blocks renders cart/checkout using React components that
   * add specific CSS classes and data attributes to their containers.
   */
  private isBlocksPage(): boolean {
    return (
      document.querySelector(".wp-block-woocommerce-checkout") !== null ||
      document.querySelector(".wp-block-woocommerce-cart") !== null ||
      document.querySelector('[data-block-name="woocommerce/checkout"]') !==
        null ||
      document.querySelector('[data-block-name="woocommerce/cart"]') !== null
    );
  }

  /**
   * Bind email field listener for Blocks-based checkout pages.
   *
   * The Blocks checkout renders asynchronously via React, so the email
   * input may not exist at load() time. Uses MutationObserver to wait
   * for the field to appear, then attaches a debounced input listener
   * and a blur listener.
   */
  private bindBlocksCheckoutEmailListener(
    analytics: SegmentflowWeb,
    data: SfWcPageData,
  ): void {
    if (data.page !== "checkout") return;

    let bound = false;

    const tryBind = (): boolean => {
      if (bound) return true;

      const selectors = [
        "#email", // WC Blocks default
        'input[id*="email"][autocomplete="email"]', // Fallback
        'input[type="email"][autocomplete="email"]', // Broader fallback
      ];

      let emailField: HTMLInputElement | null = null;
      for (const sel of selectors) {
        emailField = document.querySelector<HTMLInputElement>(sel);
        if (emailField) break;
      }
      if (!emailField) return false;

      bound = true;
      let lastCapturedEmail = "";
      const field = emailField;
      let debounceTimer: ReturnType<typeof setTimeout> | null = null;

      const captureEmail = (): void => {
        if (debounceTimer) {
          clearTimeout(debounceTimer);
          debounceTimer = null;
        }
        const email = field.value.trim();
        if (!email || email === lastCapturedEmail) return;
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;

        lastCapturedEmail = email;

        // Update sf_id cookie immediately so PHP has the email on next navigation.
        writeSfId({ e: email });

        analytics.identify({ traits: { email } });
        analytics.track({
          event: "checkout_email_captured",
          properties: {
            email,
            currency: data.currency,
            cart_total: data.cart?.total,
            item_count: data.cart?.item_count,
          },
        });
      };

      // Debounced input listener for React-controlled inputs
      field.addEventListener("input", () => {
        if (debounceTimer) clearTimeout(debounceTimer);
        debounceTimer = setTimeout(captureEmail, 1000);
      });

      // Also capture on blur for immediate firing when tabbing away
      field.addEventListener("blur", captureEmail);

      // If the field is already populated (e.g., returning customer with
      // saved billing email), capture immediately without waiting for
      // user interaction.
      if (field.value.trim()) {
        captureEmail();
      }

      return true;
    };

    // If field is already in DOM, bind immediately
    if (tryBind()) return;

    // Otherwise, observe the DOM for the email field to appear
    const observer = new MutationObserver(() => {
      if (tryBind()) {
        observer.disconnect();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // Safety: disconnect after 30s to prevent memory leaks
    setTimeout(() => observer.disconnect(), 30_000);
  }

  /**
   * Check if running on WooCommerce
   */
  isWooCommerce(): boolean {
    return this.context.isWooCommerce;
  }

  /**
   * Get captured WooCommerce context
   */
  getContext(): WooCommerceContext {
    return { ...this.context };
  }

  /**
   * Enrich track events with WooCommerce context
   */
  track(event: { context?: Record<string, unknown> }): void {
    if (!this.context.isWooCommerce || !event.context) return;

    event.context.woocommerce = {
      store_url: this.context.storeUrl,
      currency: this.context.currency,
      cart_hash: this.context.cartHash,
      customer_id: this.context.customerId,
      session_id: this.context.sessionId,
    };
  }

  /**
   * Enrich identify events with WooCommerce context
   */
  identify(event: {
    userId?: string;
    traits?: Record<string, unknown>;
    context?: Record<string, unknown>;
  }): void {
    if (!this.context.isWooCommerce || !event.context) return;

    // Add WooCommerce context
    event.context.woocommerce = {
      store_url: this.context.storeUrl,
      cart_hash: this.context.cartHash,
      session_id: this.context.sessionId,
    };

    // If we have a WooCommerce customer ID, add it to traits
    if (this.context.customerId && event.traits) {
      event.traits.woocommerce_customer_id = this.context.customerId;
    }
  }

  /**
   * Enrich page events with WooCommerce context
   */
  page(event: { context?: Record<string, unknown> }): void {
    if (!this.context.isWooCommerce || !event.context) return;

    event.context.woocommerce = {
      store_url: this.context.storeUrl,
      currency: this.context.currency,
      cart_hash: this.context.cartHash,
      session_id: this.context.sessionId,
    };
  }
}

/**
 * Create WooCommerce plugin instance
 */
export function createWooCommercePlugin(): Plugin {
  return new WooCommercePlugin();
}

export default createWooCommercePlugin;
